/**
 * QUANTITY × PRICE IN THE ADD TO CART BUTTON — 8 Sep 2026
 *
 * The button is rendered by Liquid as the variant's UNIT price and is only ever
 * rewritten by the theme when the variant changes. Nothing watches the quantity
 * box, so setting it to 6 left the button still reading $66.00 — the shopper is
 * told one number and charged six times it. Not wrong at the till, but it reads
 * as a mistake, and a price that does not move when you change the quantity is
 * the kind of thing that makes people abandon a cart.
 *
 * This keeps the button showing what the shopper is about to spend.
 *
 * Two things it has to survive:
 *   - The theme rewriting the button on a variant change. A MutationObserver
 *     catches that, re-reads the new unit price, and re-applies the multiple.
 *     Our own writes are ignored via a flag, or the observer would loop.
 *   - The colour swap, which replaces the whole product section. Re-binding on
 *     shopify:section:load picks up the new button and quantity box.
 *
 * If anything here fails the button simply keeps the theme's own text, which is
 * the price of one — never a wrong number, just a less helpful one.
 */
(function () {
  'use strict';

  var btn, qtyInput, priceEl, unitCents = null, writing = false, observer = null;

  function parseMoney(text) {
    // "$66.00", "AUD $66.00", "$1,234.56" -> cents. Returns null if unsure.
    var m = String(text || '').replace(/[^0-9.,]/g, '');
    if (!m) return null;
    // The last separator followed by exactly two digits is the decimal point;
    // anything else is a thousands separator.
    var dec = m.match(/[.,](\d{2})$/);
    var whole = dec ? m.slice(0, -3) : m;
    whole = whole.replace(/[.,]/g, '');
    if (!/^\d+$/.test(whole)) return null;
    return parseInt(whole, 10) * 100 + (dec ? parseInt(dec[1], 10) : 0);
  }

  function findPriceEl() {
    if (!btn) return null;
    // The price is the last span in the button, after the separator dot. Taking
    // the last one rather than an index keeps this working if the theme adds a
    // badge or a sale price before it.
    var spans = btn.querySelectorAll('span');
    for (var i = spans.length - 1; i >= 0; i--) {
      if (parseMoney(spans[i].textContent) !== null) return spans[i];
    }
    return null;
  }

  /**
   * Find the PRODUCT'S quantity box — scoped to the add-to-cart form.
   *
   * It used to be looked up as .QuantitySelector__CurrentQuantity, on the
   * assumption that the class belonged to the product form. It does not:
   * cart-items.liquid puts the same class on every line in the CART DRAWER,
   * and theme.liquid renders the drawer at line 205, before content_for_layout
   * at 213. So on any page where the cart was not empty, the first match in the
   * document was a drawer line, and this script multiplied the product's price
   * by the quantity of whatever happened to be first in the basket. With four of
   * something in the drawer, a $166 Set showed as $664 while the box on the page
   * said 1 — which is exactly what the client photographed.
   *
   * Scoping to the form settles it. The drawer's inputs are name="updates[]";
   * only the product form carries name="quantity". main-product.liquid renders
   * either the visible box or a hidden #BundleQtyInput — never both — so no
   * filtering on type is wanted: in bundle-pricing mode the hidden one IS the
   * real one.
   *
   * Re-resolved rather than held, because a colour swap replaces the section and
   * a detached input keeps answering with the value it had when it left the page.
   */
  function resolveQty() {
    if (qtyInput && document.contains(qtyInput)) return qtyInput;
    var form = btn && btn.form;
    qtyInput = form ? form.querySelector('[name="quantity"]') : null;
    // btn.form is null if the button is ever moved out of the form — the sticky
    // bar clones one — so fall back to the form itself rather than the document.
    if (!qtyInput) {
      qtyInput = document.querySelector('form[action*="/cart/add"] [name="quantity"]');
    }
    return qtyInput;
  }

  function qty() {
    var box = resolveQty();
    if (!box) return 1;
    var n = parseInt(box.value, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function apply() {
    if (!priceEl || unitCents === null) return;
    var total = unitCents * qty();
    var text;
    try { text = window.formatShopMoney(total); } catch (e) { return; }
    if (!text || priceEl.textContent.trim() === String(text).trim()) return;
    writing = true;
    priceEl.textContent = text;
    // A screen reader should hear the new figure, not the old one.
    btn.setAttribute('aria-label', btn.textContent.replace(/\s+/g, ' ').trim());
    setTimeout(function () { writing = false; }, 0);
  }

  // The unit price, remembered ON the element the theme itself renders.
  //
  // The old rule was "whatever money is on screen at bind time is the price of
  // one". That is true the FIRST time and false every time after, because by
  // then this script has written a total into that same element. A colour swap
  // replaces the product section and rebinds, so the total got read back as a
  // unit and multiplied again: $166 became $332 became $664 against a quantity
  // of one. A shopper was quoted four times the price of the thing she was
  // buying.
  //
  // Stashing it as an attribute fixes that for free, because the attribute
  // lives on the element the theme REPLACES. A variant change or a section swap
  // hands us a brand new span with no attribute, so we read the theme's freshly
  // rendered unit price exactly when we should, and never our own arithmetic.
  var UNIT_ATTR = 'data-unit-cents';

  function readUnit() {
    priceEl = findPriceEl();
    if (!priceEl) return false;
    var stashed = priceEl.getAttribute(UNIT_ATTR);
    if (stashed !== null && /^\d+$/.test(stashed)) { unitCents = parseInt(stashed, 10); return true; }
    var cents = parseMoney(priceEl.textContent);
    if (cents === null) return false;
    priceEl.setAttribute(UNIT_ATTR, String(cents));
    unitCents = cents;
    return true;
  }

  function bind() {
    btn = document.querySelector('.ProductForm__AddToCart');
    qtyInput = null;          // forget the old one; resolveQty() finds the live box
    resolveQty();
    if (!btn) return;
    if (observer) { observer.disconnect(); observer = null; }
    unitCents = null;
    if (!readUnit()) return;
    apply();

    // The theme rewrites the button on a variant change; re-read the unit price
    // it just wrote, then re-apply the quantity.
    observer = new MutationObserver(function () {
      if (writing) return;
      var el = findPriceEl();
      if (!el) return;
      var cents = parseMoney(el.textContent);
      if (cents === null) return;
      var expected = unitCents !== null ? unitCents * qty() : null;
      // Only treat it as a NEW unit price when it is not simply our own total.
      if (expected !== null && cents === expected) return;
      // And never when the element already carries the theme's own unit price:
      // that attribute means this span has been read once already, so anything
      // different in it now is arithmetic, not a new price.
      var stashed = el.getAttribute(UNIT_ATTR);
      if (stashed !== null && /^\d+$/.test(stashed)) {
        priceEl = el; unitCents = parseInt(stashed, 10); apply(); return;
      }
      priceEl = el;
      unitCents = cents;
      el.setAttribute(UNIT_ATTR, String(cents));
      apply();
    });
    observer.observe(btn, { childList: true, subtree: true, characterData: true });
  }

  function onQtyChange() { apply(); }

  document.addEventListener('input', function (e) {
    if (e.target && e.target.name === 'quantity') onQtyChange();
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'quantity') onQtyChange();
  });
  // The +/- buttons write the value programmatically, which fires no event in
  // some themes. A click anywhere in the quantity control re-checks on the next
  // frame, once the theme has written the new number.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.QuantitySelector, [data-action="decrease-quantity"], [data-action="increase-quantity"]')) {
      setTimeout(onQtyChange, 30);
    }
  });

  document.addEventListener('shopify:section:load', function () {
    bind();
    setTimeout(bind, 400);   // after the swapped section has settled
  });

  // The quantity box can also be written PROGRAMMATICALLY — the theme resets it
  // to 1 after a cart add, and that fires no input, change or click event. The
  // first version of this missed it and left the button reading the old total
  // against a quantity of 1, which is a wrong number rather than merely an
  // unhelpful one. Watching the value directly is the only thing that catches
  // every writer. apply() no-ops when the text is already right, so this costs
  // nothing.
  var lastSeen = null;
  setInterval(function () {
    var box = resolveQty();
    if (!box) return;
    if (box.value !== lastSeen) { lastSeen = box.value; apply(); }
  }, 250);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
